import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { UserTimeEntriesDialog } from "@/components/UserTimeEntriesDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ClockIcon } from "lucide-react";

type User = {
  id: string;
  email: string;
  created_at: string;
  role: string;
};


const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [isAdminCreatingUser, setIsAdminCreatingUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{id: string; email: string} | null>(null);
  const [createdCreds, setCreatedCreds] = useState<Array<{username: string; email: string; password: string}>>([]);
  const [isBulkCreating, setIsBulkCreating] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const params = new URLSearchParams(window.location.search);
  const isRecovery = params.get("type") === "recovery";

  useEffect(() => {
    if (isRecovery) {
      setIsResetMode(false);
    }
    checkAdminStatus();
  }, [isRecovery]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const checkAdminStatus = async () => {
    try {
      const { data: isAdminResult, error: adminCheckError } = await supabase.rpc('check_is_admin');
      
      if (adminCheckError) {
        console.error('Error checking admin status:', adminCheckError);
        return;
      }
      
      setIsAdmin(isAdminResult);
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const toEmail = (input: string) => (input.includes('@') ? input : `${input}@walia.local`);
  const generatePassword = (length = 12) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
    let pwd = '';
    for (let i = 0; i < length; i++) {
      pwd += chars[Math.floor(Math.random() * chars.length)];
    }
    return pwd;
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase.rpc('get_users_with_roles');
      if (error) throw error;
      setUsers(data);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const loginEmail = toEmail(email.trim());
      const { error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password,
      });

      if (error) throw error;

      navigate("/");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?type=recovery`,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Check your email for the password reset link",
      });
      setIsResetMode(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your password has been updated",
      });
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const identifier = email.trim();
      const normalizedEmail = toEmail(identifier);
      const baseUsername = identifier.includes('@') ? identifier.split('@')[0] : identifier;

      const { data: { user }, error: signUpError } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: window.location.origin
        }
      });

      if (signUpError) throw signUpError;

      if (user) {
        // Set profile name for UI
        await supabase
          .from('profiles')
          .upsert({ id: user.id, full_name: baseUsername });

        // Auto-create minimal employee record
        await supabase
          .from('employees')
          .insert({
            id: user.id,
            email: normalizedEmail,
            name: baseUsername,
            role: 'employee',
            status: 'active',
            work_type: 'hourly',
            salary_type: 'hourly'
          });

        toast({
          title: 'Success',
          description: 'New user created successfully',
        });
        
        setEmail('');
        setPassword('');
        fetchUsers();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSampleEmployees = async () => {
    try {
      setIsBulkCreating(true);
      const usernames = ['employee1', 'employee2'];
      const creds: Array<{ username: string; email: string; password: string }> = [];

      for (const username of usernames) {
        const emailVal = toEmail(username);
        const passwordVal = generatePassword(14);
        const { data: { user }, error } = await supabase.auth.signUp({
          email: emailVal,
          password: passwordVal,
          options: { emailRedirectTo: window.location.origin }
        });
        if (error) throw error;
        if (user) {
          await supabase.from('profiles').upsert({ id: user.id, full_name: username });
          // Try to create employee record; ignore failure if RLS prevents it
          await supabase.from('employees').insert({
            id: user.id,
            email: emailVal,
            name: username,
            role: 'employee',
            status: 'active',
            work_type: 'hourly',
            salary_type: 'hourly'
          });
          creds.push({ username, email: emailVal, password: passwordVal });
        }
      }

      setCreatedCreds(creds);
      toast({ title: 'Erfolgreich', description: '2 Mitarbeiterkonten wurden erstellt.' });
      fetchUsers();
    } catch (e: any) {
      toast({ title: 'Fehler', description: e.message || 'Konnte Benutzer nicht erstellen', variant: 'destructive' });
    } finally {
      setIsBulkCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-4xl space-y-8 p-8 bg-white rounded-lg shadow-md">
          <div className="text-center">
            <h2 className="text-2xl font-bold">
              {isAdminCreatingUser ? "Create New User" : "User Management"}
            </h2>
          </div>

          {isAdminCreatingUser ? (
            <form onSubmit={handleCreateUser} className="mt-8 space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Benutzername oder E-Mail</Label>
                  <Input
                    id="email"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <Button 
                  type="submit" 
                  className="flex-1" 
                  disabled={isLoading}
                >
                  {isLoading ? "Creating..." : "Create User"}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setIsAdminCreatingUser(false)}
                >
                  Back
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={() => setIsAdminCreatingUser(true)}
                    className="mb-4"
                  >
                    Create New User
                  </Button>
                  <Button
                    onClick={handleCreateSampleEmployees}
                    disabled={isBulkCreating}
                    className="mb-4"
                    variant="secondary"
                  >
                    {isBulkCreating ? "Creating..." : "Create Sample Employees"}
                  </Button>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/")}
                >
                  Back to Dashboard
                </Button>
              </div>

              {createdCreds.length > 0 && (
                <div className="p-4 rounded border bg-muted/20">
                  <p className="mb-2 font-medium">Neue Zugangsdaten:</p>
                  <ul className="space-y-1 text-sm">
                    {createdCreds.map((c) => (
                      <li key={c.username}>
                        <span className="font-mono">{c.username}</span> — E-Mail: <span className="font-mono">{c.email}</span> — Passwort: <span className="font-mono">{c.password}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.role}</TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedUser({ id: user.id, email: user.email })}
                          className="flex items-center gap-2"
                        >
                          <ClockIcon className="h-4 w-4" />
                          View Timings
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <UserTimeEntriesDialog
            open={selectedUser !== null}
            onOpenChange={(open) => !open && setSelectedUser(null)}
            userId={selectedUser?.id ?? ""}
            userEmail={selectedUser?.email ?? ""}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md space-y-8 p-8 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <h2 className="text-2xl font-bold">
            {isRecovery ? "Set New Password" : 
             isResetMode ? "Reset Password" : "Sign in to your account"}
          </h2>
          <p className="mt-2 text-gray-600">
            {isRecovery ? "Please enter your new password" :
             isResetMode ? "Enter your email to receive a reset link" : 
             "Please enter your credentials"}
          </p>
        </div>

        <form onSubmit={isRecovery ? handleUpdatePassword : 
                       isResetMode ? handleResetPassword : 
                       handleLogin} 
              className="mt-8 space-y-6">
          <div className="space-y-4">
            {!isRecovery && (
              <div>
                <Label htmlFor="email">E-Mail oder Benutzername</Label>
                <Input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            )}
            {(isRecovery || !isResetMode) && (
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Processing..." : 
             isRecovery ? "Update Password" :
             isResetMode ? "Send Reset Link" : 
             "Sign in"}
          </Button>
        </form>

        {!isRecovery && (
          <Button
            type="button"
            variant="link"
            className="w-full mt-2"
            onClick={() => setIsResetMode(!isResetMode)}
          >
            {isResetMode ? "Back to login" : "Forgot password?"}
          </Button>
        )}

      </div>
    </div>
  );
};

export default Auth;
